/**
 * Single SSE connection per authenticated session.
 *
 * Before this provider, each consumer (Dashboard, Onboarding,
 * LayoutRealtimeBus) opened its own EventSource — three pages open
 * → three connections to the same Redis pubsub channel, three copies
 * of every event. Acceptable at small scale but wasteful and a
 * source of subtle duplicate-handler bugs.
 *
 * Now: ONE stream opens at the layout level, role-scoped. Pages
 * register their widget refetchers and event-type handlers via the
 * `useRegisterWidget` / `useRegisterEventHandler` hooks. The provider
 * fans out incoming events to every registered subscriber.
 *
 * Registration semantics:
 *   - Widgets: last-wins per key. Two consumers registering the same
 *     widget key (e.g. 'kpis') means the second registration replaces
 *     the first. Cleanup only removes the entry if it still belongs
 *     to the deregistering consumer (identity check).
 *   - Event handlers: multi-handler per type. Many consumers can
 *     listen for the same event (e.g. 'appointment.imminent' →
 *     reminder banner + audit log + an analytics tracker). All are
 *     invoked in registration order; cleanup removes only the
 *     specific handler.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  useEventStream,
  type EventTypeHandlerMap,
  type RealtimeEvent,
  type RefetchMap,
} from '../../hooks/useEventStream';
import authService from '../../services/auth.service';
import { useAuthStore } from '../../stores/authStore';
import { API_URL } from '../../lib/config';
import { withRetry } from '../../lib/retry';

type Role = 'agent' | 'client' | 'manager' | 'super_admin' | null;

export type WidgetRefetcher = () => void | Promise<void>;
export type RealtimeEventHandler = (event: RealtimeEvent<string>) => void;

interface RealtimeContextValue {
  /** Register a widget refetcher. Returns the deregister callback. */
  registerWidget: (key: string, refetcher: WidgetRefetcher) => () => void;
  /** Register an event-type handler. Returns the deregister callback. */
  registerEventHandler: (type: string, handler: RealtimeEventHandler) => () => void;
}

// `null` sentinel = no provider mounted (e.g., outside MainLayout —
// tests, auth pages). Consumer hooks tolerate this and no-op.
const RealtimeContext = createContext<RealtimeContextValue | null>(null);


// ─── Internal: role normalisation + stream gating ─────────────────

function normalizeRole(raw: string | undefined): Role {
  if (!raw) return null;
  if (raw === 'agent' || raw === 'client' || raw === 'manager' || raw === 'super_admin') {
    return raw;
  }
  return null;
}

/** Map (role, userId) → the one SSE URL to subscribe to. Returns
 *  null when the role hasn't loaded yet (the hook treats null as
 *  "don't subscribe"). */
function resolveSseUrl(role: Role, userId: string | null): string | null {
  if (!role) return null;
  if (role === 'agent' && userId) {
    return `${API_URL}/api/v1/agents/${userId}/events`;
  }
  if (role === 'client' && userId) {
    return `${API_URL}/api/v1/clients/${userId}/events`;
  }
  if (role === 'manager' || role === 'super_admin') {
    return `${API_URL}/api/v1/staff/events`;
  }
  return null;
}

/** Build a Proxy whose `[key]` reads the latest refetcher from the
 *  ref-backed Map. Lifted to module scope to keep the provider's
 *  effect nesting depth ≤ 4 (SonarQube S2004). */
function makeWidgetProxy(
  ref: { current: Map<string, WidgetRefetcher> },
): RefetchMap<string> {
  return new Proxy<RefetchMap<string>>({}, {
    get(_, prop) {
      if (typeof prop !== 'string') return undefined;
      return ref.current.get(prop);
    },
  });
}

/** Like makeWidgetProxy but for event handlers — `[type]` returns a
 *  fan-out function that calls every currently-registered handler
 *  for that type, swallowing exceptions so one bad handler can't
 *  break the others. */
function makeEventHandlerProxy(
  ref: { current: Map<string, Set<RealtimeEventHandler>> },
): EventTypeHandlerMap<string> {
  return new Proxy<EventTypeHandlerMap<string>>({}, {
    get(_, prop) {
      if (typeof prop !== 'string') return undefined;
      const set = ref.current.get(prop);
      if (!set || set.size === 0) return undefined;
      return (event: RealtimeEvent<string>) => fanOutHandlers(set, event);
    },
  });
}

/** Iterate registered handlers, swallowing any handler's throw so
 *  the other subscribers still fire. */
function fanOutHandlers(
  handlers: Set<RealtimeEventHandler>,
  event: RealtimeEvent<string>,
): void {
  handlers.forEach((h) => {
    try {
      h(event);
    } catch (err) {
      console.error('RealtimeProvider handler threw', err);
    }
  });
}


// ─── Provider ─────────────────────────────────────────────────────

export function RealtimeProvider({ children }: Readonly<PropsWithChildren>): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const role = normalizeRole(user?.role);
  const [userId, setUserId] = useState<string | null>(null);

  // /auth/me is cached by authService — effectively free after the
  // first call. We can't read the id from the persisted auth store
  // because it only carries email + role.
  useEffect(() => {
    if (!role) {
      setUserId(null);
      return;
    }
    let cancelled = false;
    // Retried with backoff: if this lookup fails the stream never opens, and
    // every live update on the page stops with no sign. Five attempts cover a
    // cold backend or a brief outage at sign-in.
    void withRetry(() => authService.getMyId(), { attempts: 5, isCancelled: () => cancelled })
      .then((id) => {
        if (!cancelled) setUserId(id);
      })
      .catch((err: unknown) => {
        console.warn('realtime disabled: could not resolve the current user', err);
        if (!cancelled) setUserId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [role]);

  // Two registries kept in refs so the stream's stable proxies can
  // read the latest values without forcing a re-render on every
  // register / deregister.
  const widgetsRef = useRef<Map<string, WidgetRefetcher>>(new Map());
  const handlersRef = useRef<Map<string, Set<RealtimeEventHandler>>>(new Map());

  const registerWidget = useCallback(
    (key: string, refetcher: WidgetRefetcher): (() => void) => {
      widgetsRef.current.set(key, refetcher);
      return () => {
        // Identity check: don't clobber a later registration that took
        // the same key while this consumer was mounted.
        if (widgetsRef.current.get(key) === refetcher) {
          widgetsRef.current.delete(key);
        }
      };
    },
    [],
  );

  const registerEventHandler = useCallback(
    (type: string, handler: RealtimeEventHandler): (() => void) => {
      let set = handlersRef.current.get(type);
      if (!set) {
        set = new Set();
        handlersRef.current.set(type, set);
      }
      set.add(handler);
      return () => {
        const s = handlersRef.current.get(type);
        if (!s) return;
        s.delete(handler);
        if (s.size === 0) handlersRef.current.delete(type);
      };
    },
    [],
  );

  // Stable proxies passed to the underlying stream hook. The hook
  // pins them to a ref internally; we mutate the backing maps in
  // registerWidget / registerEventHandler, so lookups always reflect
  // the latest registrations without re-running the effect that opens
  // the EventSource (which would close + reopen the stream).
  const widgetMapProxy = useMemo<RefetchMap<string>>(
    () => makeWidgetProxy(widgetsRef),
    [],
  );

  const eventHandlerProxy = useMemo<EventTypeHandlerMap<string>>(
    () => makeEventHandlerProxy(handlersRef),
    [],
  );

  // Open exactly one stream based on role. The URL gate (null) is
  // internal to useEventStream so the rules-of-hooks invariant
  // stays clean across role changes.
  const sseUrl = resolveSseUrl(role, userId);
  useEventStream<string>(sseUrl, widgetMapProxy, eventHandlerProxy);

  const ctx = useMemo<RealtimeContextValue>(
    () => ({ registerWidget, registerEventHandler }),
    [registerWidget, registerEventHandler],
  );

  return <RealtimeContext.Provider value={ctx}>{children}</RealtimeContext.Provider>;
}


// ─── Consumer hooks ───────────────────────────────────────────────

/**
 * Register a widget refetcher for the lifetime of the calling
 * component. Re-registers when `key` or `refetcher` change.
 *
 * No-op when called outside a RealtimeProvider (e.g., on auth pages)
 * — the hook just doesn't subscribe.
 */
export function useRegisterWidget(key: string, refetcher: WidgetRefetcher): void {
  const ctx = useContext(RealtimeContext);
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerWidget(key, refetcher);
  }, [ctx, key, refetcher]);
}

/**
 * Register an event-type handler for the lifetime of the calling
 * component. Re-registers when `type` or `handler` change.
 *
 * Multiple handlers for the same type are allowed — they fire in
 * registration order.
 */
export function useRegisterEventHandler(
  type: string,
  handler: RealtimeEventHandler,
): void {
  const ctx = useContext(RealtimeContext);
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerEventHandler(type, handler);
  }, [ctx, type, handler]);
}
